<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-xl font-semibold text-gray-900">Upsales Integration</h2>
        <p class="text-sm text-gray-500 mt-1">Hämta data direkt från Upsales API</p>
      </div>
      <button
        class="sd-btn sd-btn--primary"
        :disabled="status.opportunities === 'loading' || status.activities === 'loading'"
        @click="fetchFromUpsales"
      >
        <BaseIcon :icon="isFetching ? 'spinner' : 'sync-alt'" :class="{ 'fa-spin': isFetching }" class="mr-2" />
        {{ isFetching ? 'Hämtar data…' : 'Hämta från Upsales' }}
      </button>
    </div>

    <!-- Status Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold text-gray-900">Affärsmöjligheter</h3>
          <span class="inline-flex" :class="statusColor(status.opportunities)">
            <BaseIcon :icon="statusIcon(status.opportunities)" :class="{ 'fa-spin': status.opportunities === 'loading' }" />
          </span>
        </div>
        <p class="text-2xl font-bold text-gray-900">{{ counts.opportunities }}</p>
        <p class="text-xs text-gray-500 mt-1">{{ statusLabel(status.opportunities) }}</p>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold text-gray-900">Möten/Aktiviteter</h3>
          <span class="inline-flex" :class="statusColor(status.activities)">
            <BaseIcon :icon="statusIcon(status.activities)" :class="{ 'fa-spin': status.activities === 'loading' }" />
          </span>
        </div>
        <p class="text-2xl font-bold text-gray-900">{{ counts.activities }}</p>
        <p class="text-xs text-gray-500 mt-1">{{ statusLabel(status.activities) }}</p>
      </div>
    </div>

    <!-- Last Fetch Info -->
    <div v-if="lastFetch" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div class="flex items-start gap-3">
        <BaseIcon icon="check-circle" class="text-blue-600 mt-0.5" />
        <div class="flex-1">
          <h4 class="font-semibold text-blue-900">Data hämtad</h4>
          <p class="text-sm text-blue-700 mt-1">
            Senast uppdaterad: {{ formatLastFetch(lastFetch) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Error Info -->
    <div v-if="lastError" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-start gap-3">
        <BaseIcon icon="exclamation-circle" class="text-red-600 mt-0.5" />
        <div class="flex-1">
          <h4 class="font-semibold text-red-900">Fel vid datainhämtning</h4>
          <p class="text-sm text-red-700 mt-1">{{ lastError.message || 'Ett okänt fel uppstod' }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const API_BASE = `${process.env.VUE_APP_SERVER_URL}/sales/pipeline`;

export default {
  name: "PipelineUpsalesDataFetcher",
  props: {
    appointmentsCount: { type: Number, default: 0 },
    opportunitiesCount: { type: Number, default: 0 },
    lastFetchedAt: { type: [String, Date], default: null },
  },
  data() {
    return {
      status: {
        opportunities: this.opportunitiesCount > 0 ? 'success' : 'idle',
        activities: this.appointmentsCount > 0 ? 'success' : 'idle',
      },
      lastFetch: this.lastFetchedAt ? new Date(this.lastFetchedAt) : null,
      lastError: null,
      counts: {
        opportunities: this.opportunitiesCount,
        activities: this.appointmentsCount,
      },
    };
  },
  computed: {
    isFetching() {
      return this.status.opportunities === 'loading' || this.status.activities === 'loading';
    },
  },
  watch: {
    appointmentsCount(val) {
      this.counts.activities = val;
      if (val > 0 && this.status.activities === 'idle') {
        this.status = { ...this.status, activities: 'success' };
      }
    },
    opportunitiesCount(val) {
      this.counts.opportunities = val;
      if (val > 0 && this.status.opportunities === 'idle') {
        this.status = { ...this.status, opportunities: 'success' };
      }
    },
    lastFetchedAt(val) {
      this.lastFetch = val ? new Date(val) : null;
    },
  },
  methods: {
    statusIcon(s) {
      switch (s) {
        case 'loading': return 'spinner';
        case 'success': return 'check-circle';
        case 'error': return 'times-circle';
        default: return 'circle';
      }
    },
    statusColor(s) {
      switch (s) {
        case 'loading': return 'text-blue-500';
        case 'success': return 'text-green-500';
        case 'error': return 'text-red-500';
        default: return 'text-gray-300';
      }
    },
    statusLabel(s) {
      switch (s) {
        case 'loading': return 'Hämtar…';
        case 'success': return 'Hämtad';
        case 'error': return 'Misslyckades';
        default: return 'Väntar';
      }
    },
    formatLastFetch(date) {
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleString('sv-SE', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },
    async fetchFromUpsales() {
      this.lastError = null;

      // 1. Opportunities -> split into pipeline + won deals
      this.status = { ...this.status, opportunities: 'loading' };
      let opportunities = [];
      try {
        const { data } = await this.axios.get(`${API_BASE}/upsales/opportunities`);
        if (!data?.success) throw new Error(data?.message || 'Hämtning misslyckades');
        opportunities = data.data || [];
        this.counts.opportunities = opportunities.length;
        this.status = { ...this.status, opportunities: 'success' };
      } catch (error) {
        console.error('Upsales opportunities error:', error);
        this.status = { ...this.status, opportunities: 'error' };
        this.lastError = { message: error.response?.data?.message || error.message };
        this.$toasted.error(`Fel vid hämtning av affärsmöjligheter: ${this.lastError.message}`, { duration: 5000 });
        return;
      }

      // 2. Appointments
      this.status = { ...this.status, activities: 'loading' };
      let appointments = [];
      try {
        const { data } = await this.axios.get(`${API_BASE}/upsales/appointments`);
        if (!data?.success) throw new Error(data?.message || 'Hämtning misslyckades');
        appointments = data.data || [];
        this.counts.activities = appointments.length;
        this.status = { ...this.status, activities: 'success' };
      } catch (error) {
        console.error('Upsales appointments error:', error);
        this.status = { ...this.status, activities: 'error' };
        // Continue anyway with opportunities
      }

      this.lastFetch = new Date();

      // Pass payload up to parent for persistence + state update
      this.$emit('data-fetched', { opportunities, appointments });

      const wonCount = opportunities.filter((o) => o.Fas === 'Vunnen').length;
      this.$toasted.success(
        `Hämtade ${opportunities.length} affärsmöjligheter (varav ${wonCount} vunna) och ${appointments.length} möten från Upsales`,
        { duration: 5000 }
      );
    },
  },
};
</script>
